import { AoInstance } from "../../ao/ao"
import { PoolLiquidity } from "../../ao/monitor-api"
import { PoolOverview } from "../../ao/router"
import ENV from "../../env"
import { PoolApiRow } from "../../types/data-source"
import { createLogger } from "../../utils/logger"

const logger = createLogger("SUPABASE-DATA-SOURCE")

interface SupabaseConfig {
  /** e.g. https://project-ref.supabase.co (NO trailing slash) */
  url: string
  /** anon or service_role key so Edge Function will run */
  apiKey: string
}

/**
 * Lightweight data-source that calls Supabase Edge Functions
 * through plain `fetch` instead of the Supabase JS client.
 */
export class SupabaseDataSource {
  private readonly cfg: SupabaseConfig

  constructor(_ao: AoInstance, cfg: Partial<SupabaseConfig> = {}) {
    this.cfg = {
      url: (cfg.url ?? ENV.VITE_SUPABASE_URL ?? "").replace(/\/$/, ""),
      apiKey: cfg.apiKey ?? ENV.VITE_SUPABASE_ANON_KEY ?? "",
    }

    if (!this.cfg.url || !this.cfg.apiKey) {
      throw new Error("SupabaseDataSource needs SUPABASE_URL and SUPABASE_ANON_KEY")
    }
  }

  /* ---------- DataSource methods ---------- */

  async getPoolsOverview(): Promise<PoolOverview[]> {
    logger.log("Fetching pools overview via fetch /overview")

    const response = await fetch(`${this.cfg.url}/functions/v1/overview`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.cfg.apiKey}`,
      },
      body: JSON.stringify({}),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    if (!data) {
      throw new Error("No response from Get-Overview (pools)")
    }

    if (!Array.isArray(data)) return []

    return data.map((pool) => ({
      quote_token_process: pool.token0_process,
      quote_token_ticker: pool.token0_ticker,
      quote_token_name: pool.token0_name,
      base_token_process: pool.token1_process,
      base_token_ticker: pool.token1_ticker,
      base_token_name: pool.token1_name,
      tvl_rank: pool.tvl_rank ?? 0,
      tvl_in_usd: pool.liquidity_usd,
      tvl_in_quote: pool.tvl_in_quote ?? 0,
      tx_count: pool.transactions,
      apr_30d: pool.apr_30d ?? 0,
      amm_process: pool.amm_process,
      pool_fee_bps: pool.pool_fee_bps,
      liquidity_usd: pool.liquidity_usd,
    }))
  }

  /*   async getUsdPrices(tokenProcesses: string[]): Promise<Record<string, PriceRow>> {
    // Nothing to do
    if (!tokenProcesses.length) return {} as Record<string, PriceRow>

    // Run all calls in parallel but keep a lid on concurrency (p-limit is tiny)
    const limit = (fn: () => Promise<any>) => fn() // swap for p-limit(4) if you like

    const entries = await Promise.all(
      tokenProcesses.map((tp) =>
        limit(async () => {
          const { data, error } = await supabase.functions.invoke<PriceRow>("usd-price", {
            headers: {
              Authorization: `Bearer ${this.cfg.apiKey}`,
            },
            body: { processId: tp },
          })

          if (error) {
            console.warn(`usd_price failed for ${tp}:`, error.message)
            return [tp, { usd_price: null, denominator: null }] as const
          }

          return [tp, data ?? { usd_price: null, denominator: null }] as const
        }),
      ),
    )

    return Object.fromEntries(entries) as Record<string, PriceRow>
  }
 */
  async getPoolsForToken(tokenProcess: string): Promise<PoolApiRow[]> {
    const response = await fetch(`${this.cfg.url}/functions/v1/pools`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.cfg.apiKey}`,
      },
      body: JSON.stringify({}),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const poolsRaw = (await response.json()) as PoolApiRow[]
    if (!poolsRaw?.length) return []

    const pools = poolsRaw.filter(
      (p) => p.token0_process === tokenProcess || p.token1_process === tokenProcess,
    )

    if (!pools.length) {
      logger.warn(`No pools found for token ${tokenProcess}`)
      return []
    }

    return pools
  }

  async getGlobalLiquidity(): Promise<PoolLiquidity[]> {
    logger.log("Fetching all pools via fetch /pools")

    const response = await fetch(`${this.cfg.url}/functions/v1/pools`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.cfg.apiKey}`,
      },
      body: JSON.stringify({}),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const poolsRaw = (await response.json()) as PoolApiRow[]
    if (!poolsRaw?.length) return []

    // Convert PoolApiRow to PoolLiquidity format
    return poolsRaw.map((pool) => ({
      amm_name: pool.amm_name,
      amm_process: pool.amm_process,
      amm_token0: pool.token0_process,
      amm_token1: pool.token1_process,
      amm_status: pool.amm_status,
      pool_fee_bps: pool.pool_fee_bps,
      token_0_decimals: pool.token0_denominator,
      token_1_decimals: pool.token1_denominator,
      reserves_0: "0", // These would need to be fetched separately
      reserves_1: "0", // These would need to be fetched separately
      created_at_ts: pool.amm_discovered_at_ts,
      amm_discovered_at_ts: pool.amm_discovered_at_ts,
      amm_base_token: pool.token0_process,
      amm_quote_token: pool.token1_process,
    }))
  }

  async getReservesForPool(
    poolId: string,
    pair: [string, string],
  ): Promise<[bigint, bigint, string]> {
    logger.log(`Fetching reserves for pool ${poolId} via fetch /pool-reserves`)

    const url =
      `${this.cfg.url}/rest/v1/global_liquidity_with_metadata` +
      `?select=reserves_token0_raw,reserves_token1_raw,token0,token1` +
      `&amm_process=eq.${encodeURIComponent(poolId)}` +
      `&limit=1`

    // Add timeout and retry logic for flaky Supabase responses
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 5000) // 5 second timeout

    try {
      const r = await fetch(url, {
        headers: {
          apikey: this.cfg.apiKey,
          Authorization: `Bearer ${this.cfg.apiKey}`,
          Accept: "application/json",
          Prefer: "return=representation",
        },
        signal: controller.signal,
        // Disable cache to avoid stale data
        cache: "no-cache",
      })

      clearTimeout(timeout)

      if (!r.ok) {
        const errorText = await r.text().catch(() => "Unable to read error response")
        throw new Error(`Supabase error ${r.status}: ${errorText}`)
      }

      // Try to parse JSON with error handling
      let data
      try {
        data = await r.json()
      } catch (jsonError) {
        logger.error("Failed to parse Supabase JSON response", jsonError)
        throw new Error("Invalid JSON response from Supabase")
      }

      if (!data || !Array.isArray(data) || data.length === 0) {
        throw new Error(`No data found for pool ${poolId} in Supabase`)
      }

      const [row] = data

      // Map reserves to the correct token order based on the pair
      const reserves0 = BigInt(row.reserves_token0_raw)
      const reserves1 = BigInt(row.reserves_token1_raw)

      // Check if the pair order matches the pool's canonical order
      // Note: columns are now 'token0' and 'token1', not 'token0_process' and 'token1_process'
      if (pair[0] === row.token0 && pair[1] === row.token1) {
        // Pair matches canonical order
        return [reserves0, reserves1, ""]
      } else if (pair[0] === row.token1 && pair[1] === row.token0) {
        // Pair is reversed from canonical order
        return [reserves1, reserves0, ""]
      } else {
        // This shouldn't happen - token mismatch
        logger.error(
          `Token mismatch: pair=[${pair[0]}, ${pair[1]}], pool tokens=[${row.token0}, ${row.token1}]`,
        )
        throw new Error(`Token mismatch for pool ${poolId}`)
      }
    } catch (error) {
      clearTimeout(timeout)

      if (error instanceof Error) {
        if (error.name === "AbortError") {
          logger.error(`Supabase request timed out for pool ${poolId}`)
          throw new Error(`Supabase request timed out`)
        }
        logger.error(`Supabase error for pool ${poolId}: ${error.message}`)
        throw error
      }

      throw new Error(`Unknown error fetching pool ${poolId} from Supabase`)
    }
  }
}
